import { useState, useEffect } from "react";
import { Container, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import axios from "axios";

//import constants
import { BREAKPOINT, WIDTH } from "../../../constants/breakpoints";
import BASE_URL from "../../../constants/baseUrls";

//import general components
import Loading from "../../common/loading/loading";
import RecommendDivider from "../../common/recommend-divider/recommend-divider";

//import view components
import RecommendedHotelItem from "./recommended-hotel-item";

//import styles
import "./recommended-hotel.scss";

export default function RecommendedHotel({ hotelList }) {
    //general variables
    const { t } = useTranslation();
    const [parent] = useAutoAnimate();
    const [loading, setLoading] = useState(false);
    const accessToken = localStorage.getItem("accessToken");

    //sessionStorage variables
    const favorites = JSON.parse(sessionStorage.getItem("favorites"));
    const { isItinerary } = JSON.parse(sessionStorage.getItem("generalInformation"));
    const { checkIn, checkOut, destination } = JSON.parse(sessionStorage.getItem("hotelInformation")) ?? {};
    const { firstDayTime, lastDayTime, startTime, endTime } = JSON.parse(sessionStorage.getItem("itineraryInformation")) ?? {};

    const userInformation = JSON.parse(localStorage.getItem("userInformation"));

    //request user favorites
    const getUserFavorites = async () => {
        try {
            const response = await axios.get(`${BASE_URL}/account/user/favorites/`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            // Filter out null values
            const favoritesData = response.data;
            const filteredFavorites = Object.fromEntries(Object.entries(favoritesData).filter(([favoriteKey, value]) => value !== null));
            sessionStorage.setItem("favorites", JSON.stringify(filteredFavorites));
        } catch (error) {
            console.error("Error fetching user favorites:", error);
        }
    };

    useEffect(() => {
        if (!favorites && accessToken) getUserFavorites();
    }, [favorites, accessToken]);

    //request recommended hotel
    const [recommendedHotels, setRecommendedHotels] = useState([]);
    const getRecommendedHotels = async () => {
        setLoading(true);
        const requestData = {
            _favourites: favorites,
            _timings: {
                start_datetime: `${checkIn}T${firstDayTime || "08:00:00"}`,
                end_datetime: `${checkOut}T${lastDayTime || "22:00:00"}`,
                custom_start_time: startTime || null,
                custom_end_time: endTime || null,
                min_lunch_start_time: "13:00:00",
                max_lunch_start_time: "15:15:00",
                min_dinner_start_time: "20:00:00",
                max_dinner_start_time: "22:30:00",
                meal_time_duration: 5400,
            },
            _travelers: {
                adults_count: 1,
                children_count: 0,
                infants_count: 0,
            },
            _user_id: accessToken ? userInformation.id : 0,
            _city_id: destination.id,
        };

        try {
            const response = await axios.post(`${BASE_URL}/api/v2/reserve/accommodation/recommender/`, requestData, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            setRecommendedHotels(response.data.data);
        } catch (error) {
            console.error("Error fetching recommended hotels:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if ((isItinerary || !!accessToken) && hotelList?.length) getRecommendedHotels();
    }, [hotelList]);

    //find common hotels between hotelList and recommendedHotels
    const findCommonHotels = (hotelList, recommendedHotels, maxResults = 3) => {
        const hotelIdSet = new Set(hotelList?.map(hotel => hotel?.id));

        return recommendedHotels.reduce((matchingHotels, hotel) => {
            if (hotelIdSet.has(hotel.id) && matchingHotels.length < maxResults) {
                matchingHotels.push(hotel);
            }
            return matchingHotels;
        }, []);
    };

    // State for top-rated hotels
    const [topRatedHotels, setTopRatedHotels] = useState([]);
    useEffect(() => {
        if (!recommendedHotels?.length || !hotelList?.length) return;

        const commonHotels = findCommonHotels(hotelList, recommendedHotels);        

        if (commonHotels.length) {
            const hotelListWithScores = hotelList.map(hotel => {
                const matchingHotel = commonHotels.find(commonHotel => commonHotel.id === hotel.id);

                const sortedPrice = hotel?.rooms?.flatMap?.(room => room.rate_plans)?.sort((a, b) => a.prices.total_price - b.prices.total_price);
                return matchingHotel ? { ...hotel, score: matchingHotel.total_score, room_min_price: sortedPrice?.[0] } : hotel;
            });

            const sortedHotels = hotelListWithScores.filter(hotel => hotel.score !== undefined).sort((a, b) => b.score - a.score);
            setTopRatedHotels(sortedHotels);
        }
    }, [recommendedHotels, hotelList]);

    // Handle dropdown expansion for mobile devices
    const [isExpanded, setIsExpanded] = useState(WIDTH > BREAKPOINT);
    const toggleExpansion = () => {
        if (WIDTH < BREAKPOINT) {
            setIsExpanded(prevState => !prevState);
        }
    };

    return (
        <>
            {loading && <Loading />}

            {topRatedHotels?.length > 0 && (
                <Grid className="recommended-hotel" container item xs={12} rowGap={{ xs: 0, md: 4 }} alignItems="flex-start" ref={parent}>
                    <RecommendDivider title={t("atripaSuggestion")} handleClick={toggleExpansion} isExpanded={isExpanded}>
                        {isExpanded && (
                            <Container maxWidth="xl">
                                <RecommendedHotelItem topRatedHotels={topRatedHotels} />
                            </Container>
                        )}
                    </RecommendDivider>
                </Grid>
            )}
        </>
    );
}
